import React, { useState, useCallback, useEffect } from 'react';
import { Modal } from '../ui';
import { groundTruthService } from '../../services';
import { domainService } from '../../services/domainService';
import { DomainSelector } from '../shared/DomainSelector';
import {
  PdfUploadFileResult,
  ExcelPreviewRow,
  ExcelPreviewResponse,
  ExcelConfirmResponse,
  Domain,
} from '../../types';
import toast from 'react-hot-toast';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void;
}

type Step = 1 | 2 | 3 | 4;

export const GroundTruthImportModal: React.FC<Props> = ({ isOpen, onClose, onImportComplete }) => {
  const [step, setStep] = useState<Step>(1);
  const [loading, setLoading] = useState(false);

  // Config — domain + duplicate mode
  const [domains, setDomains] = useState<Domain[]>([]);
  const [selectedDomain, setSelectedDomain] = useState<string>('');
  const [duplicateMode, setDuplicateMode] = useState<'skip' | 'update'>('skip');

  // Step 1 — PDF upload
  const [pdfFiles, setPdfFiles] = useState<File[]>([]);
  const [pdfResults, setPdfResults] = useState<PdfUploadFileResult[]>([]);
  const [pdfUploaded, setPdfUploaded] = useState(false);

  // Step 2 — Excel upload
  const [excelFile, setExcelFile] = useState<File | null>(null);

  // Step 3 — Preview
  const [preview, setPreview] = useState<ExcelPreviewResponse | null>(null);

  // Step 4 — Result
  const [importResult, setImportResult] = useState<ExcelConfirmResponse | null>(null);

  // Manual tags to add on import
  const [manualTags, setManualTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');

  // PROD1.7.5 — User must acknowledge invalid columns before confirming
  const [acknowledgedInvalid, setAcknowledgedInvalid] = useState(false);

  // PROD1.7.8 follow-up — politique sur cellules vides en mode update.
  // Coché (défaut) : cellules vides ignorées (préservent la BD).
  // Décoché : cellules vides remplacent la valeur BD par null (destructif).
  const [ignoreEmptyCells, setIgnoreEmptyCells] = useState(true);

  // Load domains on open
  useEffect(() => {
    if (isOpen) {
      domainService.list().then(d => {
        setDomains(d);
        if (d.length > 0 && !selectedDomain) {
          setSelectedDomain(d[0].name);
        }
      }).catch(() => {});
    }
  }, [isOpen]);

  const resetState = () => {
    setStep(1);
    setLoading(false);
    setPdfFiles([]);
    setPdfResults([]);
    setPdfUploaded(false);
    setExcelFile(null);
    setPreview(null);
    setImportResult(null);
    setDuplicateMode('skip');
    setManualTags([]);
    setTagInput('');
    setAcknowledgedInvalid(false);
    setIgnoreEmptyCells(true);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  // --- Step 1: PDF upload ---

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files).filter(
      (f) => f.name.toLowerCase().endsWith('.pdf')
    );
    setPdfFiles((prev) => [...prev, ...droppedFiles]);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selected = Array.from(e.target.files).filter(
        (f) => f.name.toLowerCase().endsWith('.pdf')
      );
      setPdfFiles((prev) => [...prev, ...selected]);
    }
  };

  const removePdfFile = (index: number) => {
    setPdfFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUploadPdfs = async () => {
    if (pdfFiles.length === 0) return;
    setLoading(true);
    try {
      const result = await groundTruthService.uploadPdfs(pdfFiles);
      setPdfResults(result.uploaded);
      setPdfUploaded(true);
      toast.success(`${result.success_count} PDF(s) uploadé(s) avec succès`);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Erreur lors de l\'upload';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // --- Step 2: Excel upload ---

  const handleExcelSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setExcelFile(e.target.files[0]);
    }
  };

  const handleAnalyzeExcel = async () => {
    if (!excelFile) return;
    setLoading(true);
    try {
      const result = await groundTruthService.importExcelPreview(excelFile, selectedDomain);
      setPreview(result);
      setStep(3);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Erreur lors de l\'analyse';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // --- Step 3: Confirm import ---

  const handleConfirmImport = async () => {
    if (!preview) return;

    // PROD1.7.8 follow-up — confirmation destructive si l'utilisateur a décoché
    // "Ignorer les champs vides" en mode update.
    const isDestructive = duplicateMode === 'update' && !ignoreEmptyCells;
    if (isDestructive) {
      const ok = window.confirm(
        `Attention — vous avez décoché "Ignorer les champs vides".\n\n` +
        `Les cellules vides du fichier vont REMPLACER les valeurs existantes en BD par null ` +
        `(pour les ${preview.duplicates_count} document(s) à mettre à jour).\n\n` +
        `Les colonnes absentes du fichier ne seront pas affectées.\n\n` +
        `Confirmer l'import ?`,
      );
      if (!ok) return;
    }

    setLoading(true);
    try {
      const result = await groundTruthService.importExcelConfirm(
        preview.preview_token, selectedDomain, duplicateMode, manualTags,
        !ignoreEmptyCells,
      );
      setImportResult(result);
      setStep(4);
      toast.success(`${result.imported_count} document(s) importé(s)`);
      onImportComplete();
    } catch (error: unknown) {
      const rawMsg = error instanceof Error ? error.message : '';
      // Le backend renvoie 400 "Invalid or expired preview token" si le token
      // a expiré (timeout long entre étapes, ou redémarrage du backend qui
      // vide le _preview_store en mémoire). On guide l'utilisateur vers une
      // nouvelle analyse plutôt que de juste afficher le message brut.
      if (rawMsg.toLowerCase().includes('preview token')) {
        toast.error(
          'L\'aperçu a expiré (probablement après un délai ou un redémarrage du serveur). '
          + 'Re-sélectionnez le fichier Excel et cliquez sur "Analyser".',
          { duration: 8000 },
        );
        setPreview(null);
        setStep(2);
      } else {
        toast.error(rawMsg || 'Erreur lors de l\'import');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success': return <span className="text-green-600">&#10003;</span>;
      case 'duplicate': return <span className="text-yellow-600">&#8212;</span>;
      case 'error': return <span className="text-red-600">&#10007;</span>;
      default: return <span className="text-gray-400">&#8230;</span>;
    }
  };

  const stepTitles: Record<Step, string> = {
    1: 'Étape 1 — Upload PDFs',
    2: 'Étape 2 — Import Excel',
    3: 'Étape 3 — Aperçu',
    4: 'Étape 4 — Résultat',
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={stepTitles[step]} size="lg">
      {/* Config bar — domain + duplicate mode */}
      <div className="bg-gray-50 rounded-lg p-4 mb-4 space-y-3">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Domaine :</label>
          <div className="flex-1">
            <DomainSelector
              domains={domains}
              value={selectedDomain}
              onChange={(d) => setSelectedDomain(d || '')}
            />
          </div>
        </div>
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Documents existants :</label>
          <div className="flex items-center gap-4">
            <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
              <input
                type="radio"
                name="duplicateMode"
                value="skip"
                checked={duplicateMode === 'skip'}
                onChange={() => setDuplicateMode('skip')}
                className="text-blue-600"
              />
              Ignorer
            </label>
            <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
              <input
                type="radio"
                name="duplicateMode"
                value="update"
                checked={duplicateMode === 'update'}
                onChange={() => setDuplicateMode('update')}
                className="text-blue-600"
              />
              Mettre à jour
            </label>
          </div>
        </div>
        {/* PROD1.7.8 follow-up — politique cellules vides en mode update */}
        {duplicateMode === 'update' && (
          <div className="pt-1">
            <label className="inline-flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={ignoreEmptyCells}
                onChange={(e) => setIgnoreEmptyCells(e.target.checked)}
                className="mt-0.5 text-blue-600"
              />
              <span>
                <span className="font-medium">Ignorer les champs vides dans le fichier pour la mise à jour</span>
                <span className="block text-xs text-gray-500 mt-0.5">
                  Coché (recommandé) : une cellule vide du fichier ne modifie pas la valeur en BD.<br />
                  Décoché : une cellule vide <strong>efface</strong> la valeur en BD (met null). Les colonnes absentes du fichier ne sont jamais affectées.
                </span>
              </span>
            </label>
          </div>
        )}
      </div>

      {/* Step indicators */}
      <div className="flex items-center gap-2 mb-6">
        {([1, 2, 3, 4] as Step[]).map((s) => (
          <div
            key={s}
            className={`flex-1 h-2 rounded ${s <= step ? 'bg-blue-600' : 'bg-gray-200'}`}
          />
        ))}
      </div>

      {/* Step 1 — Upload PDFs */}
      {step === 1 && (
        <div>
          {!pdfUploaded ? (
            <>
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors cursor-pointer"
                onClick={() => document.getElementById('pdf-file-input')?.click()}
              >
                <p className="text-gray-600 mb-2">
                  Glissez-déposez vos fichiers PDF ici
                </p>
                <p className="text-sm text-gray-400">ou cliquez pour sélectionner</p>
                <input
                  id="pdf-file-input"
                  type="file"
                  multiple
                  accept=".pdf"
                  className="hidden"
                  onChange={handleFileSelect}
                />
              </div>

              {pdfFiles.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-medium mb-2">Fichiers sélectionnés ({pdfFiles.length})</h4>
                  <ul className="space-y-1 max-h-40 overflow-y-auto">
                    {pdfFiles.map((file, i) => (
                      <li key={i} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded text-sm">
                        <span>{file.name}</span>
                        <button
                          onClick={() => removePdfFile(i)}
                          className="text-red-500 hover:text-red-700 ml-2"
                        >
                          &#10007;
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-6 flex justify-between">
                <button
                  onClick={() => setStep(2)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm"
                >
                  Passer (PDFs déjà uploadés) &rarr;
                </button>
                <button
                  onClick={handleUploadPdfs}
                  disabled={pdfFiles.length === 0 || loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {loading ? 'Upload en cours...' : 'Uploader les PDFs'}
                </button>
              </div>
            </>
          ) : (
            <>
              <h4 className="font-medium mb-2">Résultat de l'upload</h4>
              <ul className="space-y-1 max-h-40 overflow-y-auto">
                {pdfResults.map((r, i) => (
                  <li key={i} className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded text-sm">
                    {getStatusIcon(r.status)}
                    <span>{r.filename}</span>
                    {r.error && <span className="text-red-500 text-xs ml-auto">{r.error}</span>}
                  </li>
                ))}
              </ul>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setStep(2)}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Continuer &rarr;
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Step 2 — Excel upload */}
      {step === 2 && (
        <div>
          <p className="text-gray-600 mb-2">
            Sélectionnez le fichier Excel (.xlsx) contenant les données Ground Truth.
          </p>
          <div className="text-xs text-gray-400 mb-4 space-y-1">
            <p>Le fichier doit contenir au minimum la colonne <code className="bg-gray-100 px-1 rounded">FileName</code>, qui doit correspondre exactement au nom du fichier PDF.</p>
            <p>Colonnes spéciales (non ajoutées dans expected_extraction) :</p>
            <ul className="list-disc list-inside ml-2">
              <li><code className="bg-gray-100 px-1 rounded">tags</code> — ajoute des tags au document (séparés par virgule)</li>
              <li><code className="bg-gray-100 px-1 rounded">pres_id</code> — identifiant de présentation</li>
            </ul>
            <p>Les autres colonnes deviennent des champs dans expected_extraction (noms convertis en minuscules).</p>
          </div>

          <div className="flex items-center gap-4">
            <input
              type="file"
              accept=".xlsx"
              onChange={handleExcelSelect}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
          </div>

          {excelFile && (
            <p className="mt-2 text-sm text-gray-600">
              Fichier sélectionné : <strong>{excelFile.name}</strong>
            </p>
          )}

          <div className="mt-6 flex justify-between">
            <button
              onClick={() => setStep(1)}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              &larr; Retour
            </button>
            <button
              onClick={handleAnalyzeExcel}
              disabled={!excelFile || loading || !selectedDomain}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {loading ? 'Analyse en cours...' : 'Analyser'}
            </button>
          </div>
        </div>
      )}

      {/* Step 3 — Preview */}
      {step === 3 && preview && (
        <div>
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded text-center">
              <p className="text-2xl font-bold text-blue-700">{preview.total_rows}</p>
              <p className="text-sm text-blue-600">Lignes totales</p>
            </div>
            <div className={`p-4 rounded text-center ${duplicateMode === 'update' ? 'bg-blue-50' : 'bg-yellow-50'}`}>
              <p className={`text-2xl font-bold ${duplicateMode === 'update' ? 'text-blue-700' : 'text-yellow-700'}`}>
                {preview.duplicates_count}
              </p>
              <p className={`text-sm ${duplicateMode === 'update' ? 'text-blue-600' : 'text-yellow-600'}`}>
                {duplicateMode === 'update' ? 'À mettre à jour' : 'Doublons (ignorés)'}
              </p>
            </div>
            <div className="bg-red-50 p-4 rounded text-center">
              <p className="text-2xl font-bold text-red-700">{preview.missing_pdfs_count}</p>
              <p className="text-sm text-red-600">PDFs manquants</p>
            </div>
          </div>

          {preview.missing_pdfs_count > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-red-700 font-medium">
                {preview.missing_pdfs_count} document(s) n'ont pas de PDF correspondant et seront ignorés.
              </p>
            </div>
          )}

          {/* PROD1.7.5 — Column validation report */}
          {preview.columns && (
            <div className="mb-4 border rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-3 py-2 border-b">
                <h4 className="font-medium text-sm">Validation des colonnes du fichier</h4>
              </div>
              <div className="p-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <div>
                  <div className="font-medium text-green-700 mb-1">
                    Acceptées ({preview.columns.accepted.length})
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {preview.columns.accepted.map((c) => (
                      <span key={c} className="px-1.5 py-0.5 rounded text-xs bg-green-100 text-green-800 font-mono">
                        {c}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="font-medium text-gray-700 mb-1">
                    Fixes ({preview.columns.fixed.length})
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {preview.columns.fixed.map((c) => (
                      <span key={c} className="px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-700 font-mono">
                        {c}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="font-medium text-red-700 mb-1">
                    Ignorées ({preview.columns.invalid.length})
                  </div>
                  {preview.columns.invalid.length === 0 ? (
                    <div className="text-xs text-gray-500">Aucune.</div>
                  ) : (
                    <ul className="space-y-1">
                      {preview.columns.invalid.map((c, i) => (
                        <li key={i} className="text-xs">
                          <span className="font-mono px-1 bg-red-100 text-red-800 rounded">{c.original}</span>
                          <span className="ml-1 text-gray-500">— {c.reason}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
              {preview.columns.invalid.length > 0 && (
                <div className="px-3 py-2 bg-amber-50 border-t border-amber-200">
                  <label className="flex items-start gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={acknowledgedInvalid}
                      onChange={(e) => setAcknowledgedInvalid(e.target.checked)}
                      className="mt-1"
                    />
                    <span>
                      J'ai compris que les{' '}
                      <strong>{preview.columns.invalid.length}</strong> colonne(s)
                      ci-dessus seront ignorées lors de l'import.
                    </span>
                  </label>
                </div>
              )}
            </div>
          )}

          {preview.sample_rows.length > 0 && (
            <div className="overflow-x-auto">
              <h4 className="font-medium mb-2">Aperçu ({preview.sample_rows.length} lignes)</h4>
              <div className="max-h-64 overflow-y-auto">
              <table className="w-full border-collapse text-sm">
                <thead className="sticky top-0">
                  <tr className="bg-gray-100">
                    <th className="p-2 text-left">Fichier</th>
                    <th className="p-2 text-left">Domaine</th>
                    <th className="p-2 text-center">Champs</th>
                    <th className="p-2 text-left">Tags</th>
                    <th className="p-2 text-center">Doublon</th>
                    <th className="p-2 text-center">PDF</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.sample_rows.map((row: ExcelPreviewRow, i: number) => (
                    <tr key={i} className="border-b">
                      <td className="p-2">{row.filename}</td>
                      <td className="p-2">{row.domain || selectedDomain}</td>
                      <td className="p-2 text-center">{row.fields_count}</td>
                      <td className="p-2">
                        <div className="flex flex-wrap gap-1">
                          {(row.tags || []).map((tag, j) => (
                            <span key={j} className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-indigo-100 text-indigo-700">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="p-2 text-center">
                        {row.is_duplicate ? (
                          <span className={duplicateMode === 'update' ? 'text-blue-600' : 'text-yellow-600'}>
                            {duplicateMode === 'update' ? 'MAJ' : 'Oui'}
                          </span>
                        ) : (
                          <span className="text-green-600">Non</span>
                        )}
                      </td>
                      <td className="p-2 text-center">
                        {row.has_pdf ? (
                          <span className="text-green-600">&#10003;</span>
                        ) : (
                          <span className="text-red-600">&#10007;</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}

          {/* Manual tags to add to all imported documents */}
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tags à ajouter à tous les documents importés
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const trimmed = tagInput.trim();
                    if (trimmed && !manualTags.includes(trimmed)) {
                      setManualTags([...manualTags, trimmed]);
                      setTagInput('');
                    }
                  }
                }}
                className="flex-1 border rounded px-3 py-1.5 text-sm"
                placeholder="Saisir un tag et appuyer sur Enter"
              />
              <button
                type="button"
                onClick={() => {
                  const trimmed = tagInput.trim();
                  if (trimmed && !manualTags.includes(trimmed)) {
                    setManualTags([...manualTags, trimmed]);
                    setTagInput('');
                  }
                }}
                className="px-3 py-1.5 bg-gray-200 rounded hover:bg-gray-300 text-sm"
              >
                Ajouter
              </button>
            </div>
            {manualTags.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {manualTags.map((tag) => (
                  <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800">
                    {tag}
                    <button
                      type="button"
                      onClick={() => setManualTags(manualTags.filter((t) => t !== tag))}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      &times;
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="mt-6 flex justify-between">
            <button
              onClick={() => setStep(2)}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              &larr; Retour
            </button>
            <button
              onClick={handleConfirmImport}
              disabled={
                loading ||
                ((preview?.columns?.invalid?.length ?? 0) > 0 && !acknowledgedInvalid)
              }
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              title={
                (preview?.columns?.invalid?.length ?? 0) > 0 && !acknowledgedInvalid
                  ? 'Cocher la case de confirmation pour activer l\'import.'
                  : ''
              }
            >
              {loading ? 'Import en cours...' : 'Confirmer l\'import'}
            </button>
          </div>
        </div>
      )}

      {/* Step 4 — Result */}
      {step === 4 && importResult && (
        <div>
          <div className="bg-green-50 p-4 rounded mb-4 text-center">
            <p className="text-3xl font-bold text-green-700">{importResult.imported_count}</p>
            <p className="text-sm text-green-600">
              Document(s) {duplicateMode === 'update' ? 'importé(s) / mis à jour' : 'importé(s)'}
            </p>
          </div>

          {importResult.skipped_count > 0 && (
            <div className="mb-4">
              <h4 className="font-medium mb-1 text-yellow-700">
                Ignorés ({importResult.skipped_count})
              </h4>
              <p className="text-sm text-gray-500">
                {importResult.skipped_count} ligne(s) ignorée(s).
              </p>
            </div>
          )}

          {importResult.missing_pdfs.length > 0 && (
            <div className="mb-4">
              <h4 className="font-medium mb-1 text-red-700">
                PDFs manquants ({importResult.missing_pdfs.length})
              </h4>
              <ul className="text-sm text-gray-600 list-disc list-inside max-h-32 overflow-y-auto">
                {importResult.missing_pdfs.map((pdf, i) => (
                  <li key={i}>{pdf}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Fermer
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};
